/**
 * Map overlay coordinate utilities: support both global (lat/lon) and local (x,y + WKT) systems.
 * Production may provide local x,y and WKT; this module converts to/from map lat/lon for overlays.
 */

/**
 * Default global bounds (current single-tile overlay) — used when coordinate mode is 'global'.
 */
export const DEFAULT_GLOBAL_BOUNDS = {
  south: 41.581941,
  west: -109.990079,
  north: 41.631941,
  east: -109.940079,
};

/**
 * Local overlay config shape:
 * - extent: { xmin, ymin, xmax, ymax } — local coordinate extent of the overlay image
 * - originXY: { x, y } — local point that corresponds to originLatLng
 * - originLatLng: { lat, lng } — WGS84 point for origin
 * - scaleLatPerUnit: degrees per local Y unit (positive = Y north)
 * - scaleLngPerUnit: degrees per local X unit (positive = X east)
 * - wkt: optional Well-Known Text string (reference only, not parsed for bounds)
 */
export const DEFAULT_LOCAL_CONFIG = {
  extent: { xmin: 0, ymin: 0, xmax: 1000, ymax: 1000 },
  originXY: { x: 0, y: 0 },
  originLatLng: { lat: 41.606941, lng: -109.965079 },
  scaleLatPerUnit: 0.00005,
  scaleLngPerUnit: 0.00005,
  wkt: '',
  state: '',
  routeNumber: '',
  pathNameSegment: '',
};

/**
 * Compute lat/lon bounds from local extent and transform.
 * @param {object} localConfig - { extent: {xmin,ymin,xmax,ymax}, originXY, originLatLng, scaleLatPerUnit, scaleLngPerUnit }
 * @returns {{ south: number, west: number, north: number, east: number } | null}
 */
export function localExtentToLatLngBounds(localConfig) {
  if (!localConfig?.extent || !localConfig?.originLatLng || !localConfig?.originXY) return null;
  const { extent, originXY, originLatLng, scaleLatPerUnit, scaleLngPerUnit } = localConfig;
  const kLat = typeof scaleLatPerUnit === 'number' ? scaleLatPerUnit : DEFAULT_LOCAL_CONFIG.scaleLatPerUnit;
  const kLng = typeof scaleLngPerUnit === 'number' ? scaleLngPerUnit : DEFAULT_LOCAL_CONFIG.scaleLngPerUnit;
  const lat0 = originLatLng.lat;
  const lng0 = originLatLng.lng;
  const x0 = originXY.x;
  const y0 = originXY.y;

  const toLat = (y) => lat0 + (y - y0) * kLat;
  const toLng = (x) => lng0 + (x - x0) * kLng;

  const xmin = extent.xmin;
  const xmax = extent.xmax;
  const ymin = extent.ymin;
  const ymax = extent.ymax;

  const lats = [toLat(ymin), toLat(ymax)];
  const lngs = [toLng(xmin), toLng(xmax)];

  return {
    south: Math.min(...lats),
    north: Math.max(...lats),
    west: Math.min(...lngs),
    east: Math.max(...lngs),
  };
}

/**
 * Convert a single local (x, y) point to WGS84 { lat, lng }.
 * @param {number} x - local X
 * @param {number} y - local Y
 * @param {object} localConfig - same shape as for localExtentToLatLngBounds
 * @returns {{ lat: number, lng: number } | null}
 */
export function localXYToLatLng(x, y, localConfig) {
  if (!localConfig?.originLatLng || !localConfig?.originXY) return null;
  const { originXY, originLatLng, scaleLatPerUnit, scaleLngPerUnit } = localConfig;
  const kLat = typeof scaleLatPerUnit === 'number' ? scaleLatPerUnit : DEFAULT_LOCAL_CONFIG.scaleLatPerUnit;
  const kLng = typeof scaleLngPerUnit === 'number' ? scaleLngPerUnit : DEFAULT_LOCAL_CONFIG.scaleLngPerUnit;
  return {
    lat: originLatLng.lat + (y - originXY.y) * kLat,
    lng: originLatLng.lng + (x - originXY.x) * kLng,
  };
}

/**
 * Convert WGS84 { lat, lng } to local (x, y).
 * @param {number} lat
 * @param {number} lng
 * @param {object} localConfig
 * @returns {{ x: number, y: number } | null}
 */
export function latLngToLocalXY(lat, lng, localConfig) {
  if (!localConfig?.originLatLng || !localConfig?.originXY) return null;
  const { originXY, originLatLng, scaleLatPerUnit, scaleLngPerUnit } = localConfig;
  const kLat = typeof scaleLatPerUnit === 'number' ? scaleLatPerUnit : DEFAULT_LOCAL_CONFIG.scaleLatPerUnit;
  const kLng = typeof scaleLngPerUnit === 'number' ? scaleLngPerUnit : DEFAULT_LOCAL_CONFIG.scaleLngPerUnit;
  if (kLat === 0 || kLng === 0) return null;
  return {
    x: originXY.x + (lng - originLatLng.lng) / kLng,
    y: originXY.y + (lat - originLatLng.lat) / kLat,
  };
}
